<script lang="ts">
	import { Calendar } from 'lucide-svelte';
	import type { PageData } from './$types';

	export let data: PageData;

	function formatDate(date: Date): string {
		return new Intl.DateTimeFormat('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		}).format(date);
	}

	function formatDateRange(startDate: Date, endDate: Date): string {
		const startMonth = startDate.getMonth();
		const endMonth = endDate.getMonth();
		const startYear = startDate.getFullYear();
		const endYear = endDate.getFullYear();

		// Same day
		if (startDate.toDateString() === endDate.toDateString()) {
			return formatDate(startDate);
		}

		// Same month and year
		if (startMonth === endMonth && startYear === endYear) {
			return `${startDate.getDate()}-${endDate.getDate()} ${new Intl.DateTimeFormat('en-US', {
				month: 'long',
				year: 'numeric'
			}).format(endDate)}`;
		}

		// Different months or years
		return `${formatDate(startDate)} - ${formatDate(endDate)}`;
	}
</script>

<div class="container mx-auto px-4 py-8">
	<h1 class="mb-8 text-4xl font-bold text-blue-800 dark:text-blue-400">
		<Calendar class="mr-2 inline-block h-8 w-8" />
		Upcoming Events
	</h1>

	{#if data.upcomingEvents.length > 0}
		<div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
			{#each data.upcomingEvents as event}
				<a
					href="/events/{event.slug}"
					class="group rounded-lg bg-white p-6 no-underline shadow-lg transition-all hover:shadow-xl dark:bg-gray-800"
				>
					<h2
						class="mb-4 text-2xl font-semibold text-blue-700 group-hover:text-blue-500 dark:text-blue-400"
					>
						{event.title}
					</h2>
					{#if event.description}
						<p class="mb-4 text-gray-600 dark:text-gray-300">
							{event.description}
						</p>
					{/if}
					<div class="text-sm text-gray-500 dark:text-gray-400">
						{formatDateRange(event.date, event.endDate)}
						{#if event.location}
							<br />
							{event.location}
						{/if}
					</div>
				</a>
			{/each}
		</div>
	{:else}
		<p class="text-gray-600 dark:text-gray-300">No upcoming events scheduled. Check back soon!</p>
	{/if}

	{#if data.pastEvents.length > 0}
		<h2 class="mt-16 mb-8 text-3xl font-bold text-gray-700 dark:text-gray-300">Past Events</h2>
		<div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
			{#each data.pastEvents as event}
				<a
					href="/events/{event.slug}"
					class="group rounded-lg bg-gray-50 p-6 no-underline shadow-lg transition-all hover:shadow-xl dark:bg-gray-800/50"
				>
					<h2
						class="mb-4 text-2xl font-semibold text-gray-600 group-hover:text-blue-500 dark:text-gray-400"
					>
						{event.title}
					</h2>
					{#if event.description}
						<p class="mb-4 text-gray-500 dark:text-gray-400">
							{event.description}
						</p>
					{/if}
					<div class="text-sm text-gray-400 dark:text-gray-500">
						{formatDateRange(event.date, event.endDate)}
						{#if event.location}
							<br />
							{event.location}
						{/if}
					</div>
				</a>
			{/each}
		</div>
	{/if}
</div>
